import React, { useState, useRef } from 'react';
import storageService from '../services/storageService';
import { validateFileUpload } from '../utils/validation';
import { handleFirebaseError } from '../utils/errorHandler';

const SecureFileUpload = ({ 
  onUploadComplete, 
  userId, 
  passOnId = null, 
  uploadType = 'passOn', // 'passOn' or 'profile'
  maxSize = 5 * 1024 * 1024, // 5MB default
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif'],
  className = '',
  children
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset previous state
    setError(null);
    setProgress(0);

    // Validate file
    const validation = validateFileUpload(file, maxSize, allowedTypes);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setUploading(true);

    try {
      let result;
      
      if (uploadType === 'profile') {
        result = await storageService.uploadProfileImage(
          file,
          userId,
          (progressValue) => setProgress(progressValue)
        );
      } else {
        if (!passOnId) {
          throw new Error('PassOn ID is required for pass-on image uploads');
        }
        result = await storageService.uploadPassOnImage(
          file,
          userId,
          passOnId,
          (progressValue) => setProgress(progressValue)
        );
      }
      
      onUploadComplete(result);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      const errorMessage = handleFirebaseError(error);
      setError(errorMessage);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`secure-file-upload ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileUpload}
        disabled={uploading}
        style={{ display: 'none' }}
      />
      
      <div 
        className={`upload-trigger ${uploading ? 'uploading' : ''}`}
        onClick={handleFileSelect}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleFileSelect();
          }
        }}
      >
        {children || (
          <div className="default-upload-content">
            <span className="upload-icon">📁</span>
            <span className="upload-text">
              {uploading ? 'Uploading...' : 'Click to upload file'}
            </span>
          </div>
        )}
      </div>
      
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>
      )}
      
      {error && (
        <div className="error-message" role="alert">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}
      
      <div className="upload-info">
        <small>
          Max size: {formatFileSize(maxSize)} | 
          Allowed types: {allowedTypes.join(', ')}
        </small>
      </div>
    </div>
  );
};

export default SecureFileUpload;
